import { Component, Input, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { map, takeUntil, tap } from 'rxjs/operators';

import { EventTypes } from 'src/app/core/constants/event-types';
import { Validator } from 'src/app/core/constants/validators';
import { ConsoleService } from 'src/app/core/services/console.service';
import { UtilityService } from 'src/app/core/services/utility.service';
import { AppZone } from 'src/app/core/types/app-zone.type';
import { nationalCodeValidator } from 'src/app/core/validators/national-code-validator';
import {
  CreateUserInfoCommand,
  CustomResult,
  CustomResultOfZoneDto,
  UpdateUserInfoCommand,
  UpdateUserWithRolesInfoCommand,
  ZoneType,
} from '../../../../core/api/models';
import {
  OrganizationsService,
  ZonesService,
} from '../../../../core/api/services';
import { UsersService } from '../../../../core/api/services/users.service';
import { RegularExpressions } from '../../../../core/constants/regular-expressions';
import {
  DialogService,
  DialogStatus,
} from '../../../../core/services/dialog.service';
import { AppDomService } from '../../../../core/services/dom.service';
import { DateTimeConfig } from '../../../../shared/components/date-time-picker/date-time-picker.component';
import { BaseFormPageComponent } from '../../../shared/base/components/base-form-page.component';
import { AuthenticatedMobileService } from './../../../../core/api/services/authenticated-mobile.service';
import { FormPageConfig } from './../../../../core/models/form-page-config.model';
import { DateTimeService } from './../../../../core/services/date-time.service';
import { AddUserMobileDialogComponent } from './add-user-mobile-dialog/add-user-mobile-dialog.component';
import { UserAuthMobileComponent } from './user-auth-mobile/user-auth-mobile.component';
import { UserChangeMobileComponent } from './user-change-mobile/user-change-mobile.component';

@Component({
  selector: 'app-user-form',
  templateUrl: './user-form.component.html',
  styleUrls: ['./user-form.component.scss'],
  standalone: false,
})
export class UserFormComponent extends BaseFormPageComponent implements OnInit {
  //#region Properties
  UtilityService = inject(UtilityService);

  @Input('form') input_form: FormGroup;
  @Input('groupName') input_formGroupName: string;
  @Input('isCurrentUser') input_isCurrentUser: boolean;
  @Input('selectedOrganizationId') input_selectedOrganizationId: any;
  @Input() isDialogMode: boolean = false;
  @Input() isAdmin: boolean = false;
  @Input('type') userType: string;

  formPageConfig: FormPageConfig<any> = {};
  model: any = {};

  maxLenNationalCode: number = 10;
  minLenNationalCode: number = 10;

  // isMobileAuthenticated: boolean = false;

  //zones
  selectedState;
  selectedCountry = 1;

  userName: string = '';
  organizationTitle: string = '';
  //expireTime: string = "";
  isRefreshForm: boolean = false;

  dateTimeConfig: DateTimeConfig;

  get ZoneType() {
    return ZoneType;
  }

  passwordForm: FormGroup;
  twoFactorEnabled: boolean = false;
  newMobiles: string[] = [];
  mobilesForm: FormGroup[] = [];
  mobile: string;
  //#endregion

  constructor(
    protected currentService: UsersService,
    protected dialogService: DialogService,
    protected zoneService: ZonesService,
    public organizationService: OrganizationsService,
    public authenticatedMobileService: AuthenticatedMobileService,
    protected translate: TranslateService
  ) {
    super();
  }

  ngOnInit() {
    super.ngOnInit();
    this.initModel();
    this.loading = false;
    if (!this.input_selectedOrganizationId)
      this.input_selectedOrganizationId = this.currentUserInfo?.organizationId;

    this.changeFormValidations();

    this.getIsOrganizationAdmin();

    this.changePersonalInformationValidators();

    if (this.currentUserInfo.organizationId != 1)
      this.form.controls['isCanSendSmartWithoutConfirmation'].disable();
  }

  setComponentsConfig() {
    try {
      this.dateTimeConfig = {
        dateLabel: 'common.expireDate',
        timeLabel: 'common.expireTime',
        dateFormControl: this.form?.get('expireDate') as FormControl,
        timeFormControl: this.form?.get('expireTime') as FormControl,
        minDateType: 'today',
        isDefaultValue: false,
        isConvertToIsoString: false,
        responsiveClassName: 'md:tw-w-1/2',
        dateTimeValue: this.formPageConfig?.isEditMode
          ? this.model.expireDate
          : DateTimeService.combineDateTimeToUTC(
              this.model.expireDate,
              '00:00'
            ),
        isEditMode: this.formPageConfig.isEditMode,
      };
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.systemManagement
      );
    }
  }

  isEnableSystemInfo() {
    //اگر در حالت ساخت کاربر جدید و یا سوپر ادمین بود اطلاعات سیستمی را نمایش میدهد
    if (!this.formPageConfig.isEditMode || this.currentUserInfo.isSuperAdmin)
      return true;
    //اگر آیدی سازمان کاربر انتخاب شده برابر "نباشد" با آیدی سازمان جاری اطلاعات سیستمی را نمایش میدهد
    if (
      this.input_selectedOrganizationId != this.currentUserInfo.organizationId
    )
      return true;

    if (!this.currentUserInfo.isAdminOrganization) return false;

    return !this.input_isCurrentUser;
  }

  getIsOrganizationAdmin() {
    try {
      //چک میکند که آیدی کاربر جاری برابر آیدی کاربر انتخاب شده است یا خیر
      this.input_isCurrentUser =
        this.currentUserInfo?.userId ==
        +this.formPageConfig?.editData?.id?.toString();

      if (this.isEnableSystemInfo()) {
        this.form.controls['email'].enable();
        this.form.controls['organizationId'].enable();
        this.form.controls['expireDate'].enable();
        this.form.controls['isSendSmsDescriptionRequired'].enable();
        this.form.controls['isDefault'].enable();
        this.form.controls['isActiveSend'].enable();
        this.form.controls['descriptions'].enable();
        this.form.controls['isCanSeeContactsOfPrivatePhonebooks'].enable();
        this.form.controls['isCanSeeContactsOfPublicPhonebooks'].enable();
        this.form.controls['isCanSendSmartWithoutConfirmation'].enable();
      } else {
        this.form.controls['email'].disable();
        this.form.controls['organizationId'].disable();
        this.form.controls['expireDate'].disable();
        this.form.controls['expireTime'].disable();
        this.form.controls['isSendSmsDescriptionRequired'].disable();
        this.form.controls['isDefault'].disable();
        this.form.controls['isActiveSend'].disable();
        this.form.controls['descriptions'].disable();
        this.form.controls['isCanSeeContactsOfPrivatePhonebooks'].disable();
        this.form.controls['isCanSeeContactsOfPublicPhonebooks'].disable();
        this.form.controls['isCanSendSmartWithoutConfirmation'].disable();
      }
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.systemManagement
      );
    }
  }
  //#region methods

  initForm() {
    this.form = new FormGroup({
      firstName: new FormControl('', [Validators.required]),
      lastName: new FormControl('', [Validators.required]),
      fatherName: new FormControl('', [Validators.required]),
      birthDate: new FormControl(''),
      isForeigner: new FormControl(''),
      nationalCode: new FormControl('', Validators.required),
      identityNumber: new FormControl(''),
      email: new FormControl('', [Validators.email]),
      userName: new FormControl('', [Validators.required]),
      organizationId: new FormControl('', Validators.required),
      mobile: new FormControl(''),
      phone: new FormControl('', Validator.phone),
      zipCode: new FormControl('', Validator.zipCode),
      faxNumber: new FormControl('', [
        Validators.pattern(RegularExpressions.justNumber),
      ]),
      address: new FormControl('', Validators.required),
      country: new FormControl('', Validators.required),
      state: new FormControl('', Validators.required),
      zoneId: new FormControl('', Validators.required),
      twoFactorEnabled: new FormControl(''),
      expireDate: new FormControl(''),
      expireTime: new FormControl(''),
      roleId: new FormControl(''),
      isActiveSend: new FormControl(''),
      descriptions: new FormControl(''),
      isDefault: new FormControl(''),
      isSendSmsDescriptionRequired: new FormControl(''),
      isCanSeeContactsOfPublicPhonebooks: new FormControl(''),
      isCanSeeContactsOfPrivatePhonebooks: new FormControl(''),
      isCanSendSmartWithoutConfirmation: new FormControl(''),
    });
  }

  initModel() {
    try {
      this.model.isActiveSend = false;
      this.model.isDefault = false;

      //edit mode
      if (this.formPageConfig.isEditMode) {
        this.model = Object.assign({}, this.formPageConfig.editData);
        this.twoFactorEnabled = this.formPageConfig.editData.twoFactorEnabled;

        if (this.model.zoneId) this.getSelectedState(this.model.zoneId);
        // this.isMobileAuthenticated =
        //   this.formPageConfig.editData.isAuthenticatedMobile;
        let newMobiles = Object.assign([], this.model.mobile);
        this.newMobiles = newMobiles.map((item) => item.mobile);
      }

      //create mode
      else {
        this.formPageConfig.editData = {};
        this.model.expireDate = DateTimeService.getAYearLaterToIsoString();
        this.newMobiles = [];
      }
      this.setComponentsConfig();
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.systemManagement
      );
    }
  }

  changeFormValidations() {
    try {
      if (this.formPageConfig.isEditMode) {
        this.form.controls['userName'].disable();
        // this.form.controls['mobile'].clearValidators();
        // this.form.get('mobile').updateValueAndValidity();
      }

      // if (this.currentUserInfo?.isSuperAdmin || this.currentUserInfo?.isAdmin) {
      this.form.get('expireDate').setValidators(Validators.required);
      this.form.get('expireTime').setValidators(Validators.required);
      this.form.get('expireTime').updateValueAndValidity();
      this.form.get('expireDate').updateValueAndValidity();
      // }
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.systemManagement
      );
    }
  }

  //#endregion

  //#region personal information validators

  changePersonalInformationValidators() {
    this.clearPersonalInformationValidators();

    //اتباع خارجی
    if (this.model.isForeigner) {
      this.minLenNationalCode = 12;
      this.maxLenNationalCode = 12;

      this.form
        .get('nationalCode')
        .setValidators([
          Validators.required,
          Validators.maxLength(this.minLenNationalCode),
          Validators.minLength(this.maxLenNationalCode),
          Validators.pattern(RegularExpressions.justNumber),
        ]);
    }
    //اشخصاص ایرانی
    else {
      this.minLenNationalCode = 10;
      this.maxLenNationalCode = 10;

      this.form
        .get('nationalCode')
        .setValidators([
          Validators.required,
          Validators.maxLength(this.minLenNationalCode),
          Validators.minLength(this.maxLenNationalCode),
          nationalCodeValidator(),
        ]);

      this.form
        .get('identityNumber')
        .setValidators([
          Validators.required,
          Validators.maxLength(10),
          Validators.minLength(1),
          Validators.pattern(RegularExpressions.justNumber),
        ]);

      this.form.get('birthDate').setValidators([Validators.required]);
    }

    this.updatePersonalInformationValidators();
  }

  clearPersonalInformationValidators() {
    this.form.get('nationalCode').clearValidators();
    this.form.get('identityNumber').clearValidators();
    this.form.get('birthDate').clearValidators();
  }

  updatePersonalInformationValidators() {
    this.form.get('nationalCode').updateValueAndValidity();
    this.form.get('identityNumber').updateValueAndValidity();
    this.form.get('birthDate').updateValueAndValidity();
  }

  //#endregion

  //#region Api

  // Add
  baseCreateApi(model: CreateUserInfoCommand) {
    this.unSubs();

    this.loading = true;

    this.currentService
      .usersCreateUserInfoPost({ body: model })
      .pipe(this.basePipeOperators())
      .subscribe(
        (result: CustomResult) => {
          this.apiNotificationService.notifyMessage({ result, duration: 4500 });
          if (result.isSucceed) {
            this.formPageConfig.isEditMode = false;
            this.onSuccess();
          }
        },
        (error) => {
          ConsoleService.error('error', error);
        },
        () => (this.loading = false)
      );
  }

  //Update
  baseUpdateApi(model: UpdateUserWithRolesInfoCommand) {
    this.unSubs();

    this.loading = true;

    model.roleIds = this.model.roleIds;

    model.id = this.formPageConfig.editId;
    model.twoFactorEnabled = this.twoFactorEnabled;

    this.currentService
      .usersUpdateUserInfoPut({
        id: this.formPageConfig.editId,
        body: model,
      })
      .pipe(this.basePipeOperators())
      .subscribe(
        (result: CustomResult) => {
          this.apiNotificationService.notifyMessage({
            result,
            duration: this.form.controls['expireDate'].dirty ? 4500 : 2500,
          });
          if (result.isSucceed) {
            this.formPageConfig.isEditMode = false;
            this.onSuccess();
          }
        },
        (error) => {
          ConsoleService.error('error', error);
        },
        () => (this.loading = false)
      );
  }

  updateCurrentUserInfoApi(model: UpdateUserInfoCommand): void {
    this.unSubs();

    this.loading = true;

    this.currentService
      .usersUpdateCurrentUserInfoPut({ body: model })
      .pipe(this.basePipeOperators())
      .subscribe(this.baseObserver);
  }

  //اگر آیتم پر باشه بعنی حذفه
  onAddOrDeleteMobile(item?: { mobile: string }) {
    let mobileToProcess = this.mobile || item?.mobile;
    if (!mobileToProcess) return;

    this.unSubs();
    this.loading = true;
    mobileToProcess =
      this.UtilityService.getTextWithEnglishNumber(mobileToProcess);

    this.currentService
      .usersAddOrDeleteMobile({
        body: {
          mobile: mobileToProcess,
          userId: this.formPageConfig.editId,
        },
      })
      .pipe(this.basePipeOperators())
      .subscribe({
        next: (result: CustomResult) => {
          this.apiNotificationService.notifyMessage({ result });

          if (!result.isSucceed) return;

          if (item)
            // حذف موبایل
            this.model.mobile = this.model.mobile.filter(
              (m) => m.mobile !== item.mobile
            );
          else {
            // افزودن موبایل
            this.model.mobile.push({
              mobile: mobileToProcess,
              isAuthenticated: false,
            });
            this.mobile = null;
          }
        },
        error: (err) => {
          ConsoleService.error('Mobile add/delete error', err);
        },
        complete: () => {
          this.loading = false;
        },
      });
  }

  // get zone by id
  getZoneById(zoneId: number): Observable<CustomResultOfZoneDto> {
    return this.zoneService.zonesGetZoneById({ Id: zoneId });
  }

  authenticatedMobileCreate(): Observable<Blob> {
    return this.authenticatedMobileService.authenticatedMobileCreateGet();
  }

  //#endregion

  //#region zones method

  getSelectedState(cityId) {
    this.unSubs();

    //get city by zoneId
    this.getZoneById(cityId)
      .pipe(
        map((city: CustomResultOfZoneDto) => {
          this.getSelectedCountry(city?.data?.parentId);
          this.selectedState = city?.data.parentId;
          return city;
        }),
        takeUntil(this.onDestroy$)
      )
      .subscribe();
  }

  getSelectedCountry(stateId: number) {
    this.unSubs();

    //get city by zoneId
    this.getZoneById(stateId)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((state) => {
        this.selectedCountry = state.data?.parentId;
      });
  }

  onChangeZones(zoneId, zoneType: AppZone) {
    this.model.zoneId = null;

    if (zoneType === 'states') {
      this.selectedCountry = zoneId;
      this.selectedState = null;
    } else if (zoneType === 'cities') this.selectedState = zoneId;
  }

  //#endregion

  //#region mobile Authentication

  openMobileAuthenticationForm(oldMobile) {
    this.unSubs();

    let data = {
      mobile: oldMobile,
      userGuid: this.formPageConfig.editData.userGuid,
    };

    const modalResult = this.dialogService.openModal({
      component: UserAuthMobileComponent,
      data: data,
    });

    modalResult
      .afterClosed()
      .pipe(
        takeUntil(this.onDestroy$),
        tap(() => {
          AppDomService.removeModalScrollClassFromHtml();
        })
      )
      .subscribe((newMobile) => {
        if (newMobile != false && newMobile != 'waitingForInputCode') {
          let changedMobile = this.model.mobile.find(
            (item) => item.mobile == oldMobile.mobile
          );
          if (changedMobile) {
            changedMobile.mobile = newMobile;
            changedMobile.isAuthenticated = true;
          }
        }
      });
  }

  openUpdateMobileForm(oldMobile) {
    this.unSubs();

    let data = {
      mobile: oldMobile,
      userId: this.formPageConfig.editData.id,
    };

    const modalResult = this.dialogService.openModal({
      component: UserChangeMobileComponent,
      data: data,
    });

    modalResult
      .afterClosed()
      .pipe(
        takeUntil(this.onDestroy$),
        tap(() => {
          AppDomService.removeModalScrollClassFromHtml();
        })
      )
      .subscribe((newMobile) => {
        if (newMobile) {
          let changedMobile = this.model.mobile.find(
            (item) => item.mobile == oldMobile.mobile
          );
          if (changedMobile) {
            changedMobile.mobile = newMobile;
            changedMobile.isAuthenticated = false;
          }
        }
      });
  }

  //#endregion

  onReset() {
    this.model = {};
    this.initModel();
    if (!this.formPageConfig.isEditMode) {
      this.isRefreshForm = true;
      this.selectedCountry = 1;
      this.selectedState = null;
      this.formPageConfig.editData = {};
    }
  }

  onOpenAddUserMobileDialog() {
    this.unSubs();

    const modalResult = this.dialogService.openModal({
      component: AddUserMobileDialogComponent,
    });
    modalResult
      .afterClosed()
      .pipe(
        takeUntil(this.onDestroy$),
        tap(() => {
          AppDomService.removeModalScrollClassFromHtml();
        })
      )
      .subscribe((mobileNumber) => {
        if (!mobileNumber) return;
        this.mobile = mobileNumber;

        if (!this.formPageConfig?.isEditMode) this.onAddMobile();
        else this.onAddOrDeleteMobile();
      });
  }

  changeUser(event) {
    this.formPageConfig.editData.userGuid = event;
  }

  checkForValuesChanged(model: UpdateUserWithRolesInfoCommand) {
    try {
      if (
        this.form.controls['lastName'].dirty ||
        this.form.controls['fatherName'].dirty ||
        this.form.controls['birthDate'].dirty ||
        this.form.controls['nationalCode'].dirty ||
        this.form.controls['identityNumber'].dirty ||
        this.form.controls['zipCode'].dirty ||
        // this.form.controls['mobile'].dirty ||
        this.form.controls['firstName'].dirty ||
        this.form.controls['isForeigner'].dirty
      ) {
        let dialogResult = this.dialogService.confirmDialog({
          title: this.translate.instant('common.warning'),
          content: this.translate.instant('messages.deActiveAuthentication', {
            userName: this.model.userName,
          }),
          isWarning: true,
          isConfirmDelete: false,
        });
        dialogResult.afterClosed().subscribe((result) => {
          if (result) {
            if (this.input_isCurrentUser) this.updateCurrentUserInfoApi(model);
            else this.baseUpdateApi(model);
          }
        });
      } else {
        if (this.input_isCurrentUser) this.updateCurrentUserInfoApi(model);
        else this.baseUpdateApi(model);
      }
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.systemManagement
      );
    }
  }

  onSubmit() {
    this.passwordForm.markAllAsTouched();
    if (!this.isValidForm(this.form, this.tagName)) return;
    if (this.passwordForm.invalid) return;
    if (!this.isValidMobilesForm()) {
      this.emptyMobilesError();
      return;
    }
    this.model.birthDate = DateTimeService.toUTC(this.model.birthDate);
    this.model.expireDate = DateTimeService.toUTC(this.model.expireDate);
    this.model.mobile = this.newMobiles;

    this.checkCreateOrUpdate();
  }

  emptyMobilesError() {
    this.dialogService.openDialog({
      title: 'common.error',
      message: 'common.emptyMobiles',
      status: DialogStatus.error,
    });
  }

  checkCreateOrUpdate() {
    if (this.loading || !this.form.valid) return;

    if (this.formPageConfig.isEditMode)
      this.checkForValuesChanged({
        address: this.model.address,
        birthDate: this.model.birthDate,
        descriptions: this.model.descriptions,
        email: this.model.email,
        expireDate: this.model.expireDate,
        fatherName: this.model.fatherName,
        faxNumber: this.model.faxNumber,
        firstName: this.model.firstName,
        id: this.formPageConfig.editData.id,
        isActiveSend: this.model.isActiveSend,
        isDefault: this.model.isDefault,
        lastName: this.model.lastName,
        organizationId: this.model.organizationId,
        password: this.model.password,
        phone: this.model.phone,
        twoFactorEnabled: this.twoFactorEnabled,
        zipCode: this.model.zipCode,
        zoneId: this.model.zoneId,
        identityNumber: this.model.identityNumber,
        isForeigner: this.model.isForeigner,
        nationalCode: this.model.nationalCode,
        isSendSmsDescriptionRequired: this.model.isSendSmsDescriptionRequired,
        roleIds: this.model.roleIds,
        isCanSeeContactsOfPrivatePhonebooks:
          this.model.isCanSeeContactsOfPrivatePhonebooks,
        isCanSeeContactsOfPublicPhonebooks:
          this.model.isCanSeeContactsOfPublicPhonebooks,
        isCanSendSmartWithoutConfirmation:
          this.model.isCanSendSmartWithoutConfirmation,
      });
    else this.baseCreateApi(this.model);
  }

  onAddMobile() {
    try {
      this.newMobiles.push(this.mobile);
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.systemManagement
      );
    }
  }

  onRemoveMobile(index: number) {
    try {
      // if (this.newMobiles.length > 1 || this.formPageConfig?.isEditMode) {
        this.newMobiles.splice(index, 1);
        this.mobilesForm.splice(index, 1);
      // }
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.systemManagement
      );
    }
  }
  isValidMobilesForm(): boolean {
    try {
      let result: boolean = true;
      this.mobilesForm.forEach((form) => {
        if (form.invalid) {
          result = false;
          form.markAllAsTouched();
        }
      });

      return result;
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.systemManagement
      );
    }
  }
}
